import { cloneElement, FC, ReactElement, ReactNode, useEffect, useRef, useState } from 'react';

import {
	Box,
	Center,
	Popover,
	PopoverBody,
	PopoverContent,
	PopoverTrigger,
	Portal,
	Spinner,
	Stack,
	StyleProps,
	Text,
	useFormControlContext,
	useMenuSize,
} from '@bonusly/design-system';

import { fastRandomId } from '../../../utils/crypto';
import { Reveal } from '../../display/reveal';

type InlineSelectorProps = StyleProps & {
	autoCommitOnBlur?: boolean;
	autoFocus?: boolean;
	emptyState?: ReactNode;
	isDisabled?: boolean;
	limit?: number;
	onCreate?: (value: string) => void;
	onSelect: (value: any) => void;
	onSearch: (value: string) => Promise<any[]>;
	onRemove: (value: any) => void;
	onBlur?: () => void;
	placeholder?: string;
	renderCreateLabel?: (value: string) => ReactElement;
	renderMenuItem: (props: { index: number; isSelected: boolean; option: any; onSelect: (option: any) => void }) => ReactElement;
	renderSelectedItem: (props: { index: number; isSelected: boolean; onRemove: (value: any) => void; value: any }) => ReactElement;
	value?: any[];
};

enum Status {
	Idle,
	Loading,
	Complete,
}

type Query = {
	query: string;
	status: Status;
};

export const InlineSelector: FC<InlineSelectorProps> = ({
	autoCommitOnBlur = false,
	autoFocus = false,
	emptyState,
	isDisabled = false,
	limit = Infinity,
	onCreate,
	onSearch,
	onSelect,
	onRemove,
	onBlur,
	placeholder,
	renderCreateLabel,
	renderMenuItem,
	renderSelectedItem,
	value = [],
	...rest
}) => {
	const form = useFormControlContext() ?? {};
	const menuRef = useMenuSize();
	const idRef = useRef(fastRandomId()); // Generate a unique ID on mount for aria accessibility control
	const inputRef = useRef<HTMLDivElement>(null); // The "input" element itself
	const handlerRef = useRef(onSearch); // Store onSearch in a ref in case the signature changes
	const timeoutRef = useRef<ReturnType<typeof setTimeout>>(); // The timeout for searching after the text input changes
	const execRef = useRef<{ [id: string]: () => void }>({}); // A collection of IDs with associated handlers for imperative execution. See execForId()

	handlerRef.current = onSearch;

	const [options, setOptions] = useState<any[]>([]); // The options to display in the menu.
	const [status, setStatus] = useState(Status.Idle); // Derived from query queue. Used to show loading spinner
	const [hasQuery, setHasQuery] = useState(false); // Derived from query queue. Based on an actively search query, which might be different than input text
	const [hasText, setHasText] = useState(false); // Immediate feedback on the input state
	const [isFocused, setIsFocused] = useState(false); // Track if the "input" is focused or not. Must be focused for menu to be open
	const [selectedId, setSelectedId] = useState<string | null>(null); // Track the focused element for aria-activedescendant
	const [forceClose, setForceClose] = useState(false); // Allow users to press Esc to close menu. Will reopen on click or type
	const [inputValue, setInputValue] = useState('');

	// Helper functions
	const selectionId = (index: number) => `selected:${idRef.current}-${index}`; // Generate DOM IDs for selected option "pills"
	const optionId = (index: number) => `option:${idRef.current}-${index}`; // Generate DOM IDs for menu options
	const execForId = (id: string) => execRef.current[id]?.(); // Run the handler for the associated ID, if it exists

	useEffect(() => {
		if (autoFocus) {
			requestAnimationFrame(() => inputRef.current?.focus());
		}
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	const isUnderLimit = value.length < limit;
	const isMenuOpen = hasQuery && isFocused && !forceClose && isUnderLimit && !isDisabled;
	// If we pass in an onCreate then the first option in the selector list is the option to create a new record
	// We need to offset all results by this amount to ensure correct aria handling
	const indexOffset = Number(!!onCreate);

	const getSelectedOptionIndex = () => {
		const re = new RegExp(`^option:${idRef.current}-(\\d+)$`);
		const match = selectedId?.match(re);
		return match ? +match[1] : undefined;
	};

	const queryRef = useRef(
		new (class QueryQueue {
			queue: Query[] = [];

			add(query: string) {
				this.queue.push({ query, status: Status.Idle });
				this.process();
			}

			process() {
				if (!this.queue.length) {
					setStatus(Status.Idle);
					return;
				}

				const query = this.queue[0];

				switch (query.status) {
					case Status.Idle:
						this.fetchNext();
						break;
					case Status.Loading:
						break;
					case Status.Complete:
						this.queue.shift();
						this.process();
						break;
				}
			}

			async fetchNext() {
				const query = this.queue[0];
				setHasQuery(!!query.query);

				if (!query.query) {
					setOptions([]);
					this.queue.shift();
				} else {
					setStatus(Status.Loading);
					query.status = Status.Loading;

					try {
						const result = await handlerRef.current(query.query);
						query.status = Status.Complete;
						setOptions(result);
					} catch (e) {
						query.status = Status.Complete;
						setOptions([]);
					}
				}

				this.process();
			}

			flush() {
				this.queue = [];
			}
		})()
	);

	const handleInput = () => {
		const query = inputRef.current?.textContent?.trim() ?? '';
		setHasText(!!query);
		setInputValue(query);

		if (timeoutRef.current) {
			clearTimeout(timeoutRef.current);
		}

		if (!query) {
			setHasQuery(false);
			queryRef.current.flush();
		} else {
			setSelectedId(null);
			setForceClose(false);
		}

		if (isUnderLimit) {
			timeoutRef.current = setTimeout(() => {
				queryRef.current.add(query);
			}, 300);
		}
	};

	// Interaction overrides
	const handleClick = () => {
		if (isDisabled) return;
		if (document.activeElement !== inputRef.current) {
			inputRef.current?.focus();
			setForceClose(false);
		}
	};

	const handleKeydown = (e: any) => {
		if (e.key === 'Enter') {
			e.preventDefault();
			if (getSelectedOptionIndex() !== undefined && selectedId) {
				execForId(selectedId);
			} else if (isMenuOpen && options.length === 1 && !onCreate) {
				execForId(optionId(indexOffset));
			} else if (onCreate) {
				handleCreate();
			} else if (isMenuOpen) {
				if (inputRef.current) {
					inputRef.current.textContent = '';
				}
				setHasText(false);
				setHasQuery(false);
				setForceClose(true);
				queryRef.current.flush();
			}
		}

		// // Blur the input and close the menu
		if (e.key === 'Escape') {
			e.preventDefault();
			setForceClose(true);
		}

		// Delete the selected options.
		if (e.key === 'Backspace') {
			const selection = window.getSelection();

			if (selection && !selection.anchorOffset && !selection.focusOffset) {
				const id = selectionId(value.length - 1);
				const el = document.getElementById(id);
				if (el?.dataset?.selected) {
					execForId(id);
				} else {
					setSelectedId(id);
				}
			}
		}

		if (isMenuOpen && e.key === 'ArrowDown') {
			e.preventDefault();

			let index = getSelectedOptionIndex();

			if (index === undefined && options.length + indexOffset) {
				setSelectedId(optionId(0));
			} else if (index !== undefined) {
				index++;

				if (index >= options.length + indexOffset) {
					index = 0;
				}

				setSelectedId(optionId(index));
			}
		}

		if (isMenuOpen && e.key === 'ArrowUp') {
			e.preventDefault();

			let index = getSelectedOptionIndex();

			if (index === undefined && options.length + indexOffset) {
				setSelectedId(optionId(0));
			} else if (index !== undefined) {
				index--;

				if (index < 0) {
					index = options.length - 1 + indexOffset;
				}

				setSelectedId(optionId(index));
			}
		}
	};

	// Focus event entries
	const handleFocus = () => {
		setIsFocused(true);
	};

	const handleBlur = () => {
		const currentText = inputRef.current?.textContent?.trim();

		if (autoCommitOnBlur && onCreate && currentText) {
			handleCreate();
		}

		setIsFocused(false);
		onBlur?.();
	};

	// Item selection and removal
	const handleSelect = (option: any) => {
		onSelect(option);

		if (inputRef.current) {
			inputRef.current.textContent = '';
		}

		setHasText(false);
		setHasQuery(false);
	};

	const handleCreate = () => {
		onCreate?.(inputRef.current?.textContent?.trim() ?? '');

		if (inputRef.current) {
			inputRef.current.textContent = '';
		}

		setHasText(false);
		setHasQuery(false);
	};

	const handleRemove = (option: any) => {
		onRemove(option);
	};

	if (onCreate && !execRef.current[optionId(0)]) {
		execRef.current[optionId(0)] = handleCreate;
	}

	return (
		<Popover autoFocus={false} initialFocusRef={inputRef} isLazy isOpen={isMenuOpen} placement="bottom-start">
			<PopoverTrigger>
				<Box
					alignItems="center"
					bgColor="bg.surface"
					border="1px solid"
					borderColor="border.default"
					borderRadius="md"
					display="flex"
					flexWrap="wrap"
					gap={1}
					onClick={handleClick}
					p={2}
					_focus={{
						borderColor: 'border.focus',
						boxShadow: '0 0 0 1px var(--chakra-colors-border-focus)',
					}}
					_focusWithin={{
						borderColor: 'border.focus',
						boxShadow: '0 0 0 1px var(--chakra-colors-border-focus)',
					}}
					{...(form.isInvalid && {
						'data-invalid': 'true',
					})}
					_invalid={{
						borderColor: 'error.inputBorder',
						boxShadow: '0 0 0 1px #d82c2fff',
					}}
					{...(isDisabled && {
						'aria-disabled': true,
						cursor: 'not-allowed',
						opacity: 0.6,
						pointerEvents: 'none',
					})}
					{...rest}
				>
					{/* The *pills* of the selected options to render */}
					{value.map((value: any, index: number) => {
						const id = selectionId(index);
						execRef.current[id] = () => handleRemove(value);

						const node = renderSelectedItem({
							index,
							isSelected: id === selectedId,
							value,
							onRemove: (e) => {
								e.preventDefault();
								handleRemove(value);
							},
						});
						return cloneElement(node, { id, key: index });
					})}

					{/* The input field */}
					<Box
						as="span"
						display="inline-block"
						contentEditable={!isDisabled}
						flexGrow={1}
						id={`input:${idRef.current}`}
						onBlur={handleBlur}
						onFocus={handleFocus}
						onInput={handleInput}
						onKeyDown={handleKeydown}
						py={1}
						ref={inputRef}
						role="combobox"
						aria-disabled={isDisabled || undefined}
						tabIndex={isDisabled ? -1 : undefined}
						_focus={{
							outline: 'none',
						}}
						{...(isMenuOpen && {
							'aria-owns': `menu:${idRef.current}`,
							'aria-controls': `list:${idRef.current}`,
						})}
						{...(selectedId && {
							'aria-activedescendant': selectedId,
						})}
						{...(placeholder &&
							!value.length &&
							!hasText && {
								'data-placeholder': placeholder,
								_before: {
									color: 'text.softest',
									content: 'attr(data-placeholder)',
									float: 'left',
									height: 0,
									pointerEvents: 'none',
								},
							})}
					/>
				</Box>
			</PopoverTrigger>

			<Portal>
				{/* Preventing default on the mouse down ensures the input won't blur when a user clicks on a menu item.
				  Otherwise it would start a race condition where the menu might close before the click event is handled */}
				<PopoverContent id={`menu:${idRef.current}`} onMouseDown={(e) => e.preventDefault()}>
					<PopoverBody p={1}>
						<Stack as="ul" id={`list:${idRef.current}`} role="listbox" ref={menuRef} overflowY="auto">
							<Reveal if={!!inputValue && !!onCreate}>
								<Box as="li" listStyleType="none" w="full">
									<Box
										as="button"
										id={optionId(0)}
										bgColor="grayAlpha.100"
										borderRadius="sm"
										onClick={handleCreate}
										px={3}
										py={1}
										transition="background-color 0.2s ease-in-out"
										type="button"
										w="full"
										_hover={{
											bgColor: 'primary.bgAlpha',
										}}
										_selected={{
											bgColor: 'primary.bgAlpha',
										}}
										{...(selectedId === optionId(0) && {
											'data-selected': true,
										})}
									>
										{renderCreateLabel ? renderCreateLabel(inputValue) : <Text>"{inputValue}"</Text>}
									</Box>
								</Box>
							</Reveal>

							<Reveal if={!options.length && status === Status.Idle}>{emptyState}</Reveal>

							<Reveal if={status === Status.Loading && !options.length}>
								<Center py={3}>
									<Spinner />
								</Center>
							</Reveal>

							{options.map((option: any, index: number) => {
								const id = optionId(index + indexOffset);
								execRef.current[id] = () => handleSelect(option);
								const node = renderMenuItem({
									index: index,
									isSelected: id === selectedId,
									option,
									onSelect: (e) => {
										e.preventDefault();
										handleSelect(option);
									},
								});

								return (
									<Box as="li" key={index} listStyleType="none" w="full">
										{cloneElement(node, { id })}
									</Box>
								);
							})}
						</Stack>
					</PopoverBody>
				</PopoverContent>
			</Portal>
		</Popover>
	);
};
